<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import type { Swiper as SwiperClass } from 'swiper';
import { Navigation, Thumbs, FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import type { NavigationOptions } from 'swiper/types';
import type { PhotoDTO } from '~/shared/api/client/photo/photo.dto';
import {
  resizeImage,
  resizeImageSrcsetWidths,
  resizeImageAttrs,
} from '~/shared/lib/resizeImage';

const props = defineProps<{
  photos: PhotoDTO[];
  startIndex?: number;
  isOpen?: boolean;
  isMobile?: boolean;
}>();

const mainSwiper = ref<SwiperClass | null>(null);
const thumbsSwiper = ref<SwiperClass | null>(null);

const prevEl = ref<HTMLElement | null>(null);
const nextEl = ref<HTMLElement | null>(null);

const navigationOptions = ref<NavigationOptions | boolean>(false);

const goToSlide = (index = 0) => {
  if (!mainSwiper.value) return;
  mainSwiper.value.slideToLoop(index);
};

const setThumbsSwiper = (swiper: SwiperClass) => {
  thumbsSwiper.value = swiper;
};

watchEffect(() => {
  if (prevEl.value && nextEl.value) {
    navigationOptions.value = {
      prevEl: prevEl.value,
      nextEl: nextEl.value,
    } as NavigationOptions;
  } else {
    navigationOptions.value = false;
  }
});
</script>

<template>
  <div :class="$style.wrapper">
    <div :class="$style.mainSlider">
      <Swiper
        :modules="[Navigation, Thumbs]"
        :navigation="navigationOptions"
        :thumbs="{ swiper: thumbsSwiper }"
        :initial-slide="props.startIndex ?? 0"
        :slides-per-view="1"
        :class="$style.mainSlider__swiper"
        loop
        grab-cursor
      >
        <SwiperSlide
          v-for="item in props.photos"
          :key="`mainSlide${item.id}`"
          :class="$style.mainSlider__slide"
        >
          <img
            :src="resizeImage(item.photo.file_url, { width: 1440 })"
            :srcset="
              resizeImageSrcsetWidths(
                item.photo.file_url,
                [480, 720, 1080, 1440, 2160, 2880],
              ) || undefined
            "
            sizes="(max-width: 768px) 100vw, 1200px"
            :alt="item.description ?? ''"
            :class="$style.mainSlider__image"
          />
        </SwiperSlide>
      </Swiper>

      <div :class="$style.mainSlider__controls">
        <div
          ref="prevEl"
          :class="[
            $style.mainSlider__controlsItem,
            $style.mainSlider__controlsItem_prev,
          ]"
        >
          <Icon
            :class="$style.mainSlider__controlsIcon"
            name="my-icon:i-arrow-left"
          />
        </div>
        <div
          ref="nextEl"
          :class="[
            $style.mainSlider__controlsItem,
            $style.mainSlider__controlsItem_next,
          ]"
        >
          <Icon
            :class="$style.mainSlider__controlsIcon"
            name="my-icon:i-arrow-right"
          />
        </div>
      </div>
    </div>

    <div :class="$style.thumbsSlider">
      <Swiper
        :modules="[FreeMode, Thumbs]"
        :space-between="2"
        slides-per-view="auto"
        free-mode
        watch-slides-progress
        watch-slides-visibility
        :loop="false"
        grab-cursor
        simulate-touch
        @swiper="setThumbsSwiper"
      >
        <SwiperSlide
          v-for="(item, index) in props.photos"
          :key="`thumbSlide${item.id}`"
          :class="$style.thumbsSlider__slide"
        >
          <img
            :src="
              resizeImageAttrs(item.photo.file_url, {
                width: 194,
                height: 113,
                mode: 'fill',
              }).src
            "
            :srcset="
              resizeImageAttrs(item.photo.file_url, {
                width: 194,
                height: 113,
                mode: 'fill',
              }).srcset
            "
            :alt="item.description ?? ''"
            :class="$style.thumbsSlider__image"
            @click="goToSlide(index)"
          />
        </SwiperSlide>
      </Swiper>
    </div>
  </div>
</template>

<style module lang="scss">
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 100%;
  height: 850px;
}

.mainSlider {
  position: relative;
  min-height: 0;
  display: flex;
  flex-direction: column;

  &__swiper {
    max-width: 100%;
    max-height: 100%;
    min-height: 0;
  }

  &__slide {
    width: 100%;
    height: 776px;
    max-width: 100%;
    max-height: 100%;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
  }

  &__controls {
    position: absolute;
    right: 0;
    bottom: 22px;
    display: flex;
    flex-flow: row nowrap;
    border: 1px solid var(--color-black-2);
    border-right: none;
    z-index: 1;

    &Item {
      display: flex;
      width: 100px;
      height: 64px;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      background-color: var(--color-red-1);
      &:last-of-type {
        border-left: 1px solid var(--color-black-2);
      }
    }
    &Icon {
      filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.4));
    }
  }

  &__swiperContainer {
    width: 100%;
    min-height: 0;
  }
}

.thumbsSlider {
  user-select: none;
  padding-bottom: 4px;

  :global(.swiper-slide-thumb-active) {
    border-bottom-color: var(--color-red-1);
  }

  &__slide {
    width: 194px;
    height: 113px;
    position: relative;
    border-bottom: 4px solid transparent;

    &_active {
      border-bottom-color: var(--color-red-1);
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
}

@media (max-width: 1500px) {
  .mainSlider {
    &__slide {
      height: 660px;
      max-height: 75vh;
    }
  }
}

@media (max-width: 1379px) {
  .mainSlider {
    &__slide {
      height: 600px;
    }
  }
}

@media (max-width: 768px) {
  .mainSlider {
    &__slide {
      height: 450px;
    }
  }
}

@media (max-width: 600px) {
  .wrapper {
    justify-content: space-between;
    height: 100vh;
  }
  .mainSlider {
    display: flex;
    height: 100%;
    width: 100%;
    align-items: center;

    &__controls {
      position: fixed;
      left: 0;
      top: 0;
      right: 0;
      width: 100%;
      height: 0;
      border: none;

      &Item {
        position: fixed;
        top: calc((100% - 145px) / 2);
        transform: translateY(-50%);
        width: 48px;
        height: 48px;
        background-color: transparent;
        border: 1px solid var(--color-gray-line);
        box-shadow: 0 0 10px -3px rgba(0, 0, 0, 0.4);

        &:last-of-type {
          border-left: 1px solid var(--color-gray-line);
        }

        &_prev {
          left: var(--space-2x);
        }

        &_next {
          right: var(--space-2x);
        }
      }
    }
  }

  .thumbsSlider {
    padding-top: var(--space-2x);
    padding-bottom: var(--space-2x);

    &__slide {
      &:first-child {
        margin-left: var(--space-2x);
      }
      &:last-child {
        margin-right: var(--space-2x);
      }
    }
  }
}
</style>
